import { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Checkbox } from 'components/atoms/checkbox';
import { Input } from 'components/facade/input';
import { SettingsControlPanel } from 'components/molecules/settings/control-panel';
import { useTranslation } from 'hooks/use-translation';
import { useUpdateSettings } from '../use-feature-setting';
import { CheckBoxWrap, ContentBox, SettingsLineWrapper } from './components';

const StyledInput = styled(Input)`
  width: 200px;
`;

interface Props {
  creditNotePrefix: string;
  nextCreditNoteNumber: string;
  isAllowedToEditCreditNoteNumber: boolean;
}

export const CreditNoteNumberSettings: FC<Props> = ({
  creditNotePrefix,
  nextCreditNoteNumber,
  isAllowedToEditCreditNoteNumber,
}) => {
  const [creditNotePrefixValue, setCreditNotePrefixValue] =
    useState(creditNotePrefix);
  const [nextCreditNoteNumberValue, setNextCreditNoteNumberValue] =
    useState(nextCreditNoteNumber);
  const [
    allowUsersToEditCreditNoteNumber,
    setAllowUsersToEditCreditNoteNumber,
  ] = useState(isAllowedToEditCreditNoteNumber);
  const [update, isLoading] = useUpdateSettings();
  const placeholder = useTranslation(
    'settings.credit_notes.credit_note_number.prefix.placeholder',
  );
  const label = useTranslation(
    'settings.credit_notes.controls.credit_note_number.label',
  );
  const description = useTranslation(
    'settings.credit_notes.controls.credit_note_number.description',
  );

  return (
    <SettingsControlPanel label={label} descriptions={[description]}>
      <SettingsLineWrapper>
        <ContentBox>
          <FormattedMessage id="settings.credit_notes.credit_note_number.prefix" />
        </ContentBox>
        <StyledInput
          value={creditNotePrefixValue}
          onChange={(e) => setCreditNotePrefixValue(e.target.value)}
          placeholder={placeholder}
          onBlur={() => {
            if (creditNotePrefix !== creditNotePrefixValue) {
              update({
                payload: {
                  creditNotePrefix: creditNotePrefixValue,
                },
                onError: () => {
                  setCreditNotePrefixValue(creditNotePrefix);
                },
              });
            }
          }}
          disabled={isLoading}
          maxLength={20}
        />
      </SettingsLineWrapper>
      <SettingsLineWrapper>
        <ContentBox>
          <FormattedMessage id="settings.credit_notes.credit_note_number.next_credit_note_number" />
        </ContentBox>
        <StyledInput
          disabled={isLoading}
          value={nextCreditNoteNumberValue}
          onChange={(e) => setNextCreditNoteNumberValue(e.target.value)}
          onBlur={() => {
            if (nextCreditNoteNumber !== nextCreditNoteNumberValue) {
              update({
                payload: {
                  nextCreditNoteNumber: nextCreditNoteNumberValue,
                },
                onError: () => {
                  setNextCreditNoteNumberValue(nextCreditNoteNumber);
                },
              });
            }
          }}
          maxLength={20}
        />
      </SettingsLineWrapper>
      <CheckBoxWrap>
        <Checkbox
          checked={allowUsersToEditCreditNoteNumber}
          onChange={(e) => {
            setAllowUsersToEditCreditNoteNumber(e.target.checked);
            update({
              payload: {
                allowUsersToEditCreditNoteNumber: e.target.checked,
              },
              onError: () => {
                setAllowUsersToEditCreditNoteNumber(!e.target.checked);
              },
            });
          }}
        >
          <FormattedMessage id="settings.credit_notes.credit_note_number.enable_editing" />
        </Checkbox>
      </CheckBoxWrap>
    </SettingsControlPanel>
  );
};
